import {
  AddOutlined,
  DeleteOutlined,
  EditOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { deleteDoc, doc } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../../MicrospecDynamicUI/GlobalProvider';
import { getDataFromCollection, updateData } from '../../../MicrospecDynamicUI/Helpers/dataUtils';

const ManageCategories = () => {
  const navigate = useNavigate();
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const firestore = cloudProvider.firestore;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadCategories = useCallback(() => {
    if (!firestore?.instance) return;
    setLoading(true);
    getDataFromCollection(
      (cats) => {
        const sorted = [...(cats || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setCategories(sorted);
      },
      (done) => { if (done) setLoading(false); },
      firestore.instance,
      'categories',
      true,
      100
    );
  }, [firestore?.instance]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // ── Reorder ───────────────────────────────────────────────────────────────
  const handleMove = useCallback(
    async (index, direction) => {
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      if (swapIndex < 0 || swapIndex >= categories.length) return;

      // Swap positions in the array, then normalize ALL orders to sequential
      // integers. This fixes cases where stored order values are duplicates or
      // non-sequential (e.g. all default to 0 after creation).
      const updated = [...categories];
      [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
      const normalized = updated.map((cat, i) => ({ ...cat, order: i }));
      setCategories(normalized);

      try {
        await Promise.all(
          normalized.map((cat) =>
            updateData(`categories/${cat.id}`, firestore, { order: cat.order })
          )
        );
      } catch (err) {
        console.error('Error reordering categories:', err);
        loadCategories();
      }
    },
    [categories, firestore, loadCategories]
  );

  // ── Toggle published ──────────────────────────────────────────────────────
  const handleTogglePublished = useCallback(
    async (cat) => {
      const newValue = !cat.is_published;
      setCategories((prev) =>
        prev.map((c) => (c.id === cat.id ? { ...c, is_published: newValue } : c))
      );
      try {
        await updateData(`categories/${cat.id}`, firestore, { is_published: newValue });
        setSnackbar({
          open: true,
          message: newValue ? 'تم نشر الفئة' : 'تم إخفاء الفئة',
          severity: 'success',
        });
      } catch (err) {
        console.error('Error toggling category:', err);
        loadCategories();
      }
    },
    [firestore, loadCategories]
  );

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(
    async (cat) => {
      if (!window.confirm(`هل تريد حذف فئة "${cat.name}"؟ هذا الإجراء لا يمكن التراجع عنه.`)) return;
      try {
        await deleteDoc(doc(firestore.instance, 'categories', cat.id));
        setCategories((prev) => prev.filter((c) => c.id !== cat.id));
        setSnackbar({ open: true, message: 'تم حذف الفئة بنجاح', severity: 'success' });
      } catch (err) {
        console.error('Error deleting category:', err);
        setSnackbar({ open: true, message: 'حدث خطأ أثناء الحذف', severity: 'error' });
      }
    },
    [firestore]
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            إدارة الفئات
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            رتّب الفئات وعدّل بياناتها أو أضف فئات جديدة
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddOutlined />}
          onClick={() => navigate('/admin/categories/add')}
        >
          إضافة فئة
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : categories.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography color="text.secondary">لا توجد فئات حتى الآن</Typography>
          <Button
            variant="outlined"
            startIcon={<AddOutlined />}
            onClick={() => navigate('/admin/categories/add')}
            sx={{ mt: 2 }}
          >
            إضافة أول فئة
          </Button>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          {categories.map((cat, index) => (
            <Box key={cat.id}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {/* Order controls */}
                <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                  <Tooltip title="تحريك لأعلى">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleMove(index, 'up')}
                        disabled={index === 0}
                        sx={{ p: 0.25 }}
                      >
                        <KeyboardArrowUpOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="تحريك لأسفل">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleMove(index, 'down')}
                        disabled={index === categories.length - 1}
                        sx={{ p: 0.25 }}
                      >
                        <KeyboardArrowDownOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                {/* Order badge */}
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ minWidth: 20, textAlign: 'center', flexShrink: 0 }}
                >
                  {index + 1}
                </Typography>

                {/* Category image */}
                <Avatar
                  src={cat.image || ''}
                  variant="rounded"
                  sx={{ width: 52, height: 52, flexShrink: 0, bgcolor: 'grey.100' }}
                >
                  {!cat.image && (cat.name?.[0] || '؟')}
                </Avatar>

                {/* Name & description */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" fontWeight={700} noWrap>
                      {cat.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={cat.is_published ? 'منشورة' : 'مخفية'}
                      color={cat.is_published ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                  {cat.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {cat.description}
                    </Typography>
                  )}
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title={cat.is_published ? 'إخفاء' : 'نشر'}>
                    <IconButton size="small" onClick={() => handleTogglePublished(cat)}>
                      {cat.is_published
                        ? <VisibilityOffOutlined fontSize="small" />
                        : <VisibilityOutlined fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="تعديل">
                    <IconButton size="small" onClick={() => navigate(`/admin/categories/edit/${cat.id}`)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="حذف">
                    <IconButton size="small" color="error" onClick={() => handleDelete(cat)}>
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {index < categories.length - 1 && <Divider />}
            </Box>
          ))}
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ position: 'fixed', bottom: 24 }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ManageCategories;
